import { useEffect, useMemo, useState } from 'react';
import { Briefcase, Pencil, Check, X } from 'lucide-react';
import { useVertical } from '../../context/VerticalContext';
import { useFinancialContext } from '../../context/FinancialContext';
import { supabase } from '../../lib/supabase';
import { MetricCard } from './MetricCard';
import type { ProfitAndLossData } from '../../types/finance';
import { monthLabel } from '../../lib/periods';

interface Props {
    /** `YYYY-MM` the card describes — the selected month, or the current one. */
    month: string;
    pnlData: ProfitAndLossData | null;
    lastMonthPnl: ProfitAndLossData | null;
    loading: boolean;
}

const formatCurrency = (n: number) =>
    new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(n);

const formatCurrencyExact = (n: number) =>
    new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(n);

const monthKey = (d: Date) => `${d.getFullYear()}-${d.getMonth() + 1}`;

export const ThisMonthCard = ({ month, pnlData, lastMonthPnl, loading }: Props) => {
    const { activeOrgId } = useVertical();
    const { isHistoricalView } = useFinancialContext();

    const [jobsThisMonth, setJobsThisMonth] = useState<number | null>(null);
    const [jobsLastMonth, setJobsLastMonth] = useState<number | null>(null);
    const [monthlyTarget, setMonthlyTarget] = useState<number | null>(null);
    const [previousMaxRevenue, setPreviousMaxRevenue] = useState<number>(0);

    // Inline target editor state — lives on the back face
    const [editingTarget, setEditingTarget] = useState(false);
    const [targetDraft, setTargetDraft] = useState<string>('');
    const [savingTarget, setSavingTarget] = useState(false);

    useEffect(() => {
        if (!activeOrgId) return;

        // Instants at local midnight — job timestamps compare as instants, so
        // toISOString() is correct here (unlike for a ledger DATE).
        const [year, mon] = month.split('-').map(Number);
        const startOfMonth = new Date(year, mon - 1, 1).toISOString();
        const endOfMonth = new Date(year, mon, 1).toISOString();
        const startOfLastMonth = new Date(year, mon - 2, 1).toISOString();
        const endOfLastMonth = startOfMonth;

        let cancelled = false;
        const fetchAll = async () => {
            // Jobs are created with status='pending' and progress through
            // pending → in_progress → waiting_parts → ready → completed.
            // The user perceives any started job as "this month's work" — so
            // we count anything that isn't a stub draft or cancelled.
            const activeStatusFilter = '(draft,cancelled)';

            const [currentJobs, lastJobs, orgRow, allIncome] = await Promise.all([
                supabase
                    .from('jobs')
                    .select('id', { count: 'exact', head: true })
                    .eq('org_id', activeOrgId)
                    .is('deleted_at', null)
                    .not('status', 'in', activeStatusFilter)
                    .gte('created_at', startOfMonth)
                    .lt('created_at', endOfMonth),
                supabase
                    .from('jobs')
                    .select('id', { count: 'exact', head: true })
                    .eq('org_id', activeOrgId)
                    .is('deleted_at', null)
                    .not('status', 'in', activeStatusFilter)
                    .gte('created_at', startOfLastMonth)
                    .lt('created_at', endOfLastMonth),
                supabase.from('organizations').select('monthly_jobs_target').eq('id', activeOrgId).maybeSingle(),
                supabase
                    .from('ledger_headers')
                    .select('total_gross, vat_amount, date')
                    .eq('org_id', activeOrgId)
                    .eq('transaction_type', 'income')
                    .is('deleted_at', null),
            ]);
            // Superseded by a newer month: don't paint its counts under this label.
            if (cancelled) return;

            setJobsThisMonth(currentJobs.count ?? 0);
            setJobsLastMonth(lastJobs.count ?? 0);
            setMonthlyTarget((orgRow.data as { monthly_jobs_target: number | null } | null)?.monthly_jobs_target ?? null);

            if (allIncome.data) {
                const byMonth = new Map<string, number>();
                const currentKey = monthKey(new Date());
                for (const row of allIncome.data as Array<{ total_gross: number | string; vat_amount: number | string | null; date: string }>) {
                    const d = new Date(row.date);
                    const k = monthKey(d);
                    if (k === currentKey) continue; // exclude current month from "previous max"
                    const net = Number(row.total_gross) - Number(row.vat_amount ?? 0);
                    byMonth.set(k, (byMonth.get(k) ?? 0) + net);
                }
                setPreviousMaxRevenue(byMonth.size ? Math.max(...byMonth.values()) : 0);
            }
        };

        fetchAll().catch((err) => console.error('ThisMonth fetch failed', err));
        return () => { cancelled = true; };
    }, [activeOrgId, month]);

    const revenue = pnlData?.revenue_total ?? 0;
    const prevRevenue = lastMonthPnl?.revenue_total ?? 0;
    const avgJobValue = jobsThisMonth && jobsThisMonth > 0 ? revenue / jobsThisMonth : 0;
    const prevAvgJobValue = jobsLastMonth && jobsLastMonth > 0 ? prevRevenue / jobsLastMonth : 0;
    const hasData = !loading && (revenue > 0 || (jobsThisMonth ?? 0) > 0);
    const isBestMonth = !isHistoricalView && revenue > 0 && revenue > previousMaxRevenue;

    // ── Front face composition ────────────────────────────────
    const mainFigure = hasData ? formatCurrency(revenue) : '—';

    const subline = hasData ? (
        <span>
            {jobsThisMonth ?? 0} job{(jobsThisMonth ?? 0) === 1 ? '' : 's'} ·{' '}
            {avgJobValue > 0 ? `${formatCurrency(avgJobValue)} avg` : '— avg'}
        </span>
    ) : (
        <span className="text-fg-4">No jobs recorded yet. Your first one will look good here.</span>
    );

    const topBadge = isBestMonth ? (
        <span className="inline-flex items-center gap-1 text-fd-3xs font-display font-semibold uppercase tracking-[0.16em] text-gold">
            <span aria-hidden="true">★</span> Best month yet
        </span>
    ) : null;

    // ── Back face composition ────────────────────────────────
    const revenueDelta = useMemo(() => formatPercentDelta(revenue, prevRevenue), [revenue, prevRevenue]);
    const jobsDelta = useMemo(() => formatCountDelta(jobsThisMonth ?? 0, jobsLastMonth ?? 0), [jobsThisMonth, jobsLastMonth]);
    const avgDelta = useMemo(() => formatPercentDelta(avgJobValue, prevAvgJobValue), [avgJobValue, prevAvgJobValue]);

    const saveTarget = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!activeOrgId) return;
        const n = parseInt(targetDraft, 10);
        if (!Number.isFinite(n) || n < 0) return;
        setSavingTarget(true);
        const { error } = await supabase
            .from('organizations')
            .update({ monthly_jobs_target: n || null })
            .eq('id', activeOrgId);
        setSavingTarget(false);
        if (!error) {
            setMonthlyTarget(n || null);
            setEditingTarget(false);
        }
    };

    const openTargetEditor = (e: React.MouseEvent) => {
        e.stopPropagation();
        setTargetDraft(monthlyTarget !== null ? String(monthlyTarget) : '');
        setEditingTarget(true);
    };

    const backContent = (
        <div className="h-full flex flex-col">
            <h4 className="text-fd-sm-tight font-semibold text-gold uppercase tracking-[0.18em] mb-4 flex items-center gap-2 font-display">
                <Briefcase className="w-4 h-4" /> vs last month
            </h4>
            <div className="space-y-1 flex-1">
                <DeltaRow label="Revenue" current={formatCurrencyExact(revenue)} delta={revenueDelta} />
                <DeltaRow label="Jobs" current={String(jobsThisMonth ?? 0)} delta={jobsDelta} />
                <DeltaRow label="Avg job value" current={avgJobValue > 0 ? formatCurrencyExact(avgJobValue) : '—'} delta={avgDelta} />
            </div>

            {/* Target row — progress bar OR inline prompt OR inline editor */}
            <div className="mt-3 pt-3 border-t border-line">
                {editingTarget ? (
                    // The This Month card is ~25% of the dashboard width on desktop, so
                    // there isn't room for a labelled "Set target" button alongside the
                    // input. Icon-only confirm + cancel, and input gets min-w-0 so it
                    // shrinks before overflowing the card.
                    <div
                        className="flex items-center gap-1.5 min-w-0"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <input
                            type="number"
                            min={0}
                            inputMode="numeric"
                            value={targetDraft}
                            onChange={(e) => setTargetDraft(e.target.value)}
                            placeholder="Target"
                            autoFocus
                            className="flex-1 min-w-0 h-9 px-2 bg-canvas border border-line focus:border-gold focus:shadow-focus rounded-md text-sm font-mono text-fg outline-none transition-all"
                        />
                        <button
                            onClick={saveTarget}
                            disabled={savingTarget || !targetDraft.trim()}
                            aria-label="Save target"
                            className="h-9 w-9 shrink-0 grid place-items-center fd-btn-primary rounded-md disabled:opacity-50 transition-colors"
                        >
                            <Check className="w-4 h-4" />
                        </button>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setEditingTarget(false);
                            }}
                            aria-label="Cancel"
                            className="h-9 w-9 shrink-0 grid place-items-center text-fg-3 hover:text-fg hover:bg-card rounded-md transition-colors"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                ) : monthlyTarget && monthlyTarget > 0 ? (
                    <button
                        onClick={openTargetEditor}
                        className="w-full text-left group/target"
                    >
                        <div className="flex justify-between items-center text-fd-sm-tight mb-1.5">
                            <span className="text-fg-3 font-medium uppercase tracking-wider">
                                {jobsThisMonth ?? 0} of {monthlyTarget} jobs this month
                            </span>
                            <Pencil className="w-3 h-3 text-fg-4 group-hover/target:text-gold transition-colors" />
                        </div>
                        <div className="h-1.5 rounded-full bg-card overflow-hidden">
                            <div
                                className="h-full bg-gold-fill transition-all duration-500"
                                style={{ width: `${Math.min(100, ((jobsThisMonth ?? 0) / monthlyTarget) * 100)}%` }}
                            />
                        </div>
                    </button>
                ) : (
                    <button
                        onClick={openTargetEditor}
                        className="text-fd-sm-tight text-fg-4 hover:text-gold transition-colors"
                    >
                        Set a monthly target →
                    </button>
                )}
            </div>
        </div>
    );

    return (
        <MetricCard
            title={isHistoricalView ? monthLabel(month) : 'This Month'}
            mainFigure={mainFigure}
            subline={subline}
            topBadge={topBadge}
            backContent={backContent}
            loading={loading}
        />
    );
};

// ── Helpers ────────────────────────────────────────────────────────
function formatPercentDelta(current: number, previous: number): DeltaInfo {
    if (!previous) return { value: '—', color: 'soft' };
    const pct = ((current - previous) / previous) * 100;
    const up = pct >= 0;
    return {
        value: `${up ? '↑' : '↓'} ${Math.abs(pct).toFixed(0)}%`,
        color: up ? 'success' : 'danger',
    };
}

function formatCountDelta(current: number, previous: number): DeltaInfo {
    const diff = current - previous;
    if (diff === 0) return { value: '—', color: 'soft' };
    const up = diff > 0;
    return {
        value: `${up ? '↑' : '↓'} ${Math.abs(diff)}`,
        color: up ? 'success' : 'danger',
    };
}

interface DeltaInfo {
    value: string;
    color: 'success' | 'danger' | 'soft';
}

const DeltaRow = ({ label, current, delta }: { label: string; current: string; delta: DeltaInfo }) => (
    <div className="flex justify-between items-center text-sm border-b border-line py-2.5">
        <span className="text-fg-2">{label}</span>
        <span className="flex items-center gap-3">
            <span className="font-mono text-fg tabular-nums">{current}</span>
            <span
                className={`font-mono text-xs tabular-nums w-12 text-right ${
                    delta.color === 'success'
                        ? 'text-success'
                        : delta.color === 'danger'
                          ? 'text-danger'
                          : 'text-fg-4'
                }`}
            >
                {delta.value}
            </span>
        </span>
    </div>
);
